// Requirements\RequirementsList.js

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
          Table, Button, Badge, Spinner, Alert, Card, Nav, Dropdown, ButtonGroup, Form
        } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
          faEdit, faFileLines, faFileInvoice, faTrash, faSync,
          faCalendarAlt, faListAlt, faLayerGroup, faSort,
          faHome, faCar, faUtensils, faShoppingCart, faStethoscope, faDumbbell,
          faGraduationCap, faPlug, faWifi, faMobileAlt, faTv, faPlane,
          faPaw, faGift, faPiggyBank, faCreditCard, faMoneyBill, faHammer,
          faTshirt, faBus, faFutbol, faLaptop, faSeedling, faTools, faPaintBrush,
          faReceipt, faChevronDown, faChevronRight, faGripVertical, faUniversity
        } from '@fortawesome/free-solid-svg-icons';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { getRequirements, generateBill, generateAllDueBills } from '../../services/RequirementService';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { updateCategoryOrder } from '../../services/CategoryService';
import {
  calculatePeriodTotals,
  calculateFortnightlyAmount,
  isRequirementEffectiveForFortnight,
  getRequirementFortnightStatus,
  formatEffectiveRange,
  REQUIREMENT_DISPLAY_KINDS,
  sumRequiredFortnightlyForRequirements,
  groupRequirementsForDisplay,
} from '../../utils/requirementCalculations';
import RequirementsFortnightBar from './RequirementsFortnightBar';
import RequirementsToolbarMenu from './RequirementsToolbarMenu';
import BankAccountRequirementsView from './bankAccount/BankAccountRequirementsView';
import useRequirementsMobileLayout from '../../hooks/useRequirementsMobileLayout';
import ConfirmationModal from '../ConfirmationModal';
import GenerateBillsModal from '../Forms/GenerateBillsModal';
import RelatedBillsModal from './RelatedBillsModal';
import ShadowBoxTable from '../ShadowBoxTable';
import '../../css/Bills.css'; // Reuse the Bills styling
import { useDragAndDrop } from '../../utils/dragAndDrop';
import PropTypes from 'prop-types';

// Add all icons to the library
library.add(
  faHome, faCar, faUtensils, faShoppingCart, faStethoscope, faDumbbell,
  faGraduationCap, faPlug, faWifi, faMobileAlt, faTv, faPlane,
  faPaw, faGift, faPiggyBank, faCreditCard, faMoneyBill, faHammer,
  faTshirt, faBus, faFutbol, faLaptop, faSeedling, faTools, faPaintBrush,
  faReceipt, faChevronDown, faChevronRight, faGripVertical, faUniversity
);

const RequirementsList = ({ 
  requirements,
  fortnightIndex = 0,
  fortnightDatesIndex = null,
  setFortnightIndex,
  formattedRangeFortnight = '',
  fortnightStart = null,
  fortnightEnd = null,
  onEdit, 
  onDelete, 
  onGenerateBills, 
  onGenerateAllDue, 
  onUpdateOrder,
  defaultViewMode = 'bank_account',
  onBankSheetOpenChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showGenerateAllModal, setShowGenerateAllModal] = useState(false);
  const [generatingBill, setGeneratingBill] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showAdvancedGenerateModal, setShowAdvancedGenerateModal] = useState(false);
  const [selectedRequirementId, setSelectedRequirementId] = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('requirementsViewMode');
    return savedViewMode || defaultViewMode;
  });
  const [showRelatedBillsModal, setShowRelatedBillsModal] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [kindFilter, setKindFilter] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({}); // Default all categories to collapsed
  const [expandedBankAccounts, setExpandedBankAccounts] = useState({});
  const [openToolbarMenu, setOpenToolbarMenu] = useState(null);
  const isMobileLayout = useRequirementsMobileLayout();
  const bankAccountMobile = viewMode === 'bank_account' && isMobileLayout;
  const {
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
    draggedItem,
    draggedOverItem,
    dropPosition,
    isDragging
  } = useDragAndDrop(requirements, async (updatedItems) => {
    try {
      // The items already have the correct format: {id, order}
      const updatedCategories = updatedItems;

      await updateCategoryOrder(updatedCategories);
    } catch (error) {
      console.error('Error in useDragAndDrop callback:', error.name);
      console.error('Error details:', error.response?.data);
    }
  });

  // Save viewMode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('requirementsViewMode', viewMode);
  }, [viewMode]);

  const handleGenerateBill = async (requirementId) => {
    setGeneratingBill(requirementId);
    try {
      const result = await generateBill(requirementId);

      // Check for the new response structure
      if (result.next_due_date) {
        setSuccessMessage(`Bill generated successfully! Next due date: ${formatDate(result.next_due_date)}`);
      } else {
        setSuccessMessage(`Bill generated successfully!`);
      }

      // Refresh the requirements to get the new order
      onUpdateOrder();
    } catch (err) {
      setError('Failed to generate bill. Please try again.');
    } finally {
      setGeneratingBill(null);
      // Clear success message after 5 seconds
      if (successMessage) {
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    }
  };

  const handleGenerateAllDue = async () => {
    setLoading(true);
    try {
      const result = await generateAllDueBills();
      setSuccessMessage(`${result.bill_ids.length} bills generated successfully!`);
      onUpdateOrder();
      setShowGenerateAllModal(false);
    } catch (err) {
      setError('Failed to generate bills. Please try again.');
    } finally {
      setLoading(false);
      // Clear success message after 5 seconds
      if (successMessage) {
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    }
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      'weekly': 'Weekly',
      'fortnightly': 'Fortnightly',
      'monthly': 'Monthly',
      'quarterly': 'Quarterly',
      'yearly': 'Yearly'
    };
    return labels[frequency] || frequency;
  };

  const handleAdvancedGenerate = (requirementId) => {
    setSelectedRequirementId(requirementId);
    setShowAdvancedGenerateModal(true);
  };

  const handleAdvancedGenerateSuccess = (result) => {
    setSuccessMessage(`${result.bills_generated || 1} bills generated successfully!`);
    onUpdateOrder();
    // Clear success message after 5 seconds
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  const handleViewBills = (requirement) => {
    setSelectedRequirement(requirement);
    setShowRelatedBillsModal(true);
  };

  const displayRequirements = useMemo(() => {
    if (!fortnightStart || !fortnightEnd) return requirements;
    return groupRequirementsForDisplay(requirements, fortnightStart, fortnightEnd);
  }, [requirements, fortnightStart, fortnightEnd]);

  const filterForFortnightView = (reqs) => {
    let list = reqs;
    if (kindFilter !== 'all') {
      list = list.filter((req) => req.display_kind === kindFilter);
    }
    if (!fortnightStart || !fortnightEnd) return list;
    if (showInactive) return list;
    return list.filter((req) =>
      isRequirementEffectiveForFortnight(req, fortnightStart, fortnightEnd)
    );
  };

  const filteredRequirements = filterForFortnightView(displayRequirements);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleBankAccount = (accountKey) => {
    setExpandedBankAccounts((prev) => ({
      ...prev,
      [accountKey]: !prev[accountKey],
    }));
  };

  // Add a function to render periodic totals
  const renderPeriodTotals = (items) => {
    const requiredFortnightly =
      fortnightStart && fortnightEnd
        ? sumRequiredFortnightlyForRequirements(items, fortnightStart, fortnightEnd)
        : null;
    const totals = calculatePeriodTotals(items, true, {
      fortnightStart,
      fortnightEnd,
    });
    
    return (
      <div className="d-flex align-items-center">
        <span className="me-3 fw-bold">
          {requiredFortnightly != null ? (
            <>
              {formatCurrency(requiredFortnightly)}{' '}
              <span className="text-muted fs-6">/ fn required</span>
            </>
          ) : (
            <>
              {formatCurrency(totals.fortnightly)} <span className="text-muted fs-6">/ fortnight</span>
            </>
          )}
        </span>
        <span className="text-muted" style={{ fontSize: '0.8em' }}>
          ({formatCurrency(totals.yearly)} / year)
        </span>
      </div>
    );
  };

  if (loading && requirements.length === 0) {
    return <div className="text-center p-5"><Spinner animation="border" /></div>;
  }

  // Function to organize requirements by category
  const getCategoryGroupMeta = (categoryReqs, categoryName) => {
    const details = categoryReqs[0]?.category_details;
    return {
      id: details?.id ?? `uncategorized-${categoryName}`,
      icon: details?.icon ?? 'money-bill',
      color: details?.color ?? '#6c757d',
    };
  };

  const getRequirementsByCategory = () => {
    const categorized = {};

    displayRequirements.forEach(req => {
      const categoryName = req.category_name || 'Uncategorized';
      if (!categorized[categoryName]) {
        categorized[categoryName] = [];
      }
      categorized[categoryName].push(req);
    });

    // Sort categories by their order
    return Object.keys(categorized)
      .sort((a, b) => {
        const orderA = categorized[a][0]?.category_details?.order || 0;
        const orderB = categorized[b][0]?.category_details?.order || 0;
        return orderA - orderB;
      })
      .map(category => ({
        category,
        requirements: categorized[category].sort((a, b) => {
          const orderA = a.order || 0;
          const orderB = b.order || 0;
          return orderA - orderB;
        })
      }));
  };

  // Function to sort requirements by next due date
  const getRequirementsByDueDate = () => {
    return [...displayRequirements].sort((a, b) => {
      // Sort by next_unpaid_date if available, otherwise use next_due_date
      const dateA = a.next_unpaid_date || a.next_due_date;
      const dateB = b.next_unpaid_date || b.next_due_date;

      if (!dateA && !dateB) return 0;
      if (!dateA) return 1;
      if (!dateB) return -1;

      return new Date(dateA) - new Date(dateB);
    });
  };

  // Helper function to get the FontAwesome icon component based on icon name
  const getCategoryIcon = (iconName) => {
    // Map the icon names from the backend to FontAwesome icon names
    const iconMap = {
      'home': faHome,                // House/Home
      'car': faCar,                  // Car
      'utensils': faUtensils,        // Food/Dining
      'shopping-cart': faShoppingCart, // Shopping
      'medical': faStethoscope,      // Healthcare (using faStethoscope instead of faMedical)
      'dumbbell': faDumbbell,        // Fitness
      'graduation-cap': faGraduationCap, // Education
      'plug': faPlug,                // Utilities
      'wifi': faWifi,                // Internet
      'mobile-alt': faMobileAlt,     // Phone
      'tv': faTv,                    // Entertainment
      'plane': faPlane,              // Travel
      'paw': faPaw,                  // Pets
      'gift': faGift,                // Gifts
      'piggy-bank': faPiggyBank,     // Savings
      'credit-card': faCreditCard,   // Credit Card
      'money-bill': faMoneyBill,     // Bills
      'hammer': faHammer,            // Maintenance
      'tshirt': faTshirt,            // Clothing
      'bus': faBus,                  // Transport
      'soccer-ball': faFutbol,       // Sport (using faFutbol instead of faSoccerBall)
      'laptop': faLaptop,            // Technology
      'seedling': faSeedling,        // Gardening
      'tools': faTools,              // Renovations
      'paint-brush': faPaintBrush    // Hobby
    };

    return iconMap[iconName] || faMoneyBill; // Default to money-bill if icon not found
  };

  // Render the table based on view mode
  const renderStatusBadge = (req) => {
    if (!fortnightStart || !fortnightEnd) return null;
    const status = getRequirementFortnightStatus(req, fortnightStart, fortnightEnd);
    const config = {
      active: { bg: 'success', label: 'Active' },
      not_yet: { bg: 'secondary', label: 'Not yet' },
      ended: { bg: 'warning', label: 'Ended', text: 'dark' },
    };
    const { bg, label, text } = config[status] || config.active;
    return (
      <Badge bg={bg} text={text} className="requirements-status-badge">
        {label}
      </Badge>
    );
  };

  const renderKindBadge = (req) => {
    const label = REQUIREMENT_DISPLAY_KINDS[req.display_kind] || req.display_kind || 'Allocation';
    const variant =
      req.display_kind === 'bill_actual'
        ? 'primary'
        : req.display_kind === 'bill_estimated'
          ? 'info'
          : 'secondary';
    return (
      <Badge bg={variant} className="me-1 requirements-kind-badge">
        {label}
      </Badge>
    );
  };

  const renderFundingFlags = (req) => {
    const flags = [];
    if (req.is_account_base) flags.push('Base allocation (min)');
    if (req.additive_to_total) flags.push('Additive');
    if (req.exclude_from_required_amount) flags.push('Excl. transfer');
    if (!flags.length) return null;
    return (
      <div className="small text-muted mt-1">{flags.join(' · ')}</div>
    );
  };

  const renderRequirementsTable = (reqs) => {
    return (
      <ShadowBoxTable>
        <Table striped hover responsive style={{ textAlign: "center", verticalAlign: "middle" }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Amount</th>
              <th>Frequency</th>
              <th>Status</th>
              <th>Effective</th>
              <th>Next Due</th>
              <th>Auto-generate</th>
              <th>{viewMode === 'bank_account' ? 'Category' : 'Bank Account'}</th>
              <th style={{ width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {reqs.length === 0 ? (
              <tr>
                <td colSpan="10" className="text-center">No requirements for this fortnight. Try &quot;Show inactive&quot; or another fortnight.</td>
              </tr>
            ) : (
              reqs.map(req => {
                const status =
                  fortnightStart && fortnightEnd
                    ? getRequirementFortnightStatus(req, fortnightStart, fortnightEnd)
                    : 'active';
                const rowInactive = showInactive && status !== 'active';
                return (
                <tr
                  key={req.id}
                  className={`bill-row ${rowInactive ? 'requirements-row-inactive' : ''}${
                    req.is_account_base ? ' requirements-row-base-allocation' : ''
                  }`}
                  style={{ height: '40px' }}
                >
                  <td style={{ textAlign: 'left', padding: '0px' }}>
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      {req.description && (
                        <OverlayTrigger
                          placement="right"
                          overlay={
                            <Tooltip>
                              <div style={{ whiteSpace: 'pre-wrap' }}>{req.description}</div>
                            </Tooltip>
                          }
                        >
                          <FontAwesomeIcon
                            icon={faFileLines}
                            style={{
                              position: 'absolute',
                              left: '2px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: 'rgba(108, 117, 125, 0.6)',
                              cursor: 'help',
                              fontSize: '14px',
                              marginRight: '8px'
                            }}
                          />
                        </OverlayTrigger>
                      )}
                      <h5 style={{margin: 20 }}>{req.name}</h5>
                    </div>
                  </td>
                  <td>
                    {renderKindBadge(req)}
                    {renderFundingFlags(req)}
                  </td>
                  <td>
                    {formatCurrency(req.amount)}
                    {req.frequency !== 'fortnightly' && (
                      <div style={{ fontSize: '0.8em', color: '#6c757d', fontWeight: 'lighter' }}>
                        ({formatCurrency(calculateFortnightlyAmount(req))}/fortnight)
                      </div>
                    )}
                  </td>
                  <td>{getFrequencyLabel(req.frequency)}</td>
                  <td>{renderStatusBadge(req)}</td>
                  <td className="requirements-effective-col">{formatEffectiveRange(req)}</td>
                  <td>
                    {req.display_kind === 'allocation' ? (
                      '—'
                    ) : req.next_unpaid_date ? (
                      formatDate(req.next_unpaid_date)
                    ) : (
                      'none'
                    )}
                  </td>
                  <td>
                    {req.display_kind === 'allocation' ? (
                      <span className="text-muted">—</span>
                    ) : (
                      <Badge bg={req.auto_generate ? 'success' : 'secondary'}>
                        {req.auto_generate ? 'Auto' : 'Off'}
                      </Badge>
                    )}
                  </td>
                  <td>
                    {viewMode === 'bank_account' ? (
                      // Show category when grouped by bank account
                      req.category_name ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              {req.category_name}
                            </Tooltip>
                          }
                        >
                          <span>
                            <FontAwesomeIcon 
                              icon={getCategoryIcon(req.category_details?.icon)} 
                              style={{ 
                                color: req.category_details?.color || '#6c757d',
                                fontSize: '1.2em' 
                              }} 
                            />
                          </span>
                        </OverlayTrigger>
                      ) : (
                        <span className="text-muted">-</span>
                      )
                    ) : (
                      // Show bank account for other view modes
                      req.bank_account_details ? (
                        <OverlayTrigger
                          placement="top"
                          overlay={
                            <Tooltip>
                              {req.bank_account_details.nickname} - {req.bank_account_details.bank} ({req.bank_account_details.acc_num})
                            </Tooltip>
                          }
                        >
                          <span>{req.bank_account_details.nickname}</span>
                        </OverlayTrigger>
                      ) : (
                        <span className="text-muted">No account</span>
                      )
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <OverlayTrigger
                        placement="top"
                        overlay={(
                          <Tooltip>
                            {fortnightIndex < 0
                              ? 'Cannot edit requirements for past fortnights'
                              : 'Edit Requirement'}
                          </Tooltip>
                        )}
                      >
                        <span className="d-inline-block">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="me-1"
                            style={{ width: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                            onClick={() => onEdit(req)}
                            disabled={fortnightIndex < 0}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                        </span>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>Generate Bill</Tooltip>}
                      >
                        <Button
                          variant="outline-success"
                          size="sm"
                          className="me-1"
                          style={{ width: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                          onClick={() => handleAdvancedGenerate(req.id)}
                        >
                          <FontAwesomeIcon icon={faFileInvoice} />
                        </Button>
                      </OverlayTrigger>

                      <OverlayTrigger
                        placement="top"
                        overlay={<Tooltip>View Bills</Tooltip>}
                      >
                        <Button
                          variant="outline-info"
                          size="sm"
                          style={{ width: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                          onClick={() => handleViewBills(req)}
                        >
                          <FontAwesomeIcon icon={faReceipt} />
                        </Button>
                      </OverlayTrigger>
                    </div>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </Table>
      </ShadowBoxTable>
    );
  };

  return (
    <div className="container-fluid">
      {error && <Alert variant="danger">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      {!bankAccountMobile && (
        <div className="section-heading-container requirements-page-heading">
          <h2 className="section-heading-inline">
            <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: "10px" }} />
            Requirements
          </h2>
        </div>
      )}

      {setFortnightIndex && (
        <RequirementsFortnightBar
          fortnightIndex={fortnightIndex}
          fortnightDatesIndex={fortnightDatesIndex}
          setFortnightIndex={setFortnightIndex}
          formattedRangeFortnight={formattedRangeFortnight}
          fortnightStart={fortnightStart}
          fortnightEnd={fortnightEnd}
          requirements={requirements}
        />
      )}

      <div
        className={`requirements-list-toolbar mb-3${
          bankAccountMobile ? ' requirements-list-toolbar--mobile' : ''
        }`}
      >
        <div className="requirements-list-toolbar-row">
          <RequirementsToolbarMenu
            label="Group by"
            menuId="requirements-view-mode-menu"
            isOpen={openToolbarMenu === 'view'}
            onOpenChange={(open) => setOpenToolbarMenu(open ? 'view' : null)}
          >
            <button
              type="button"
              role="menuitem"
              className={`requirements-toolbar-menu-item${viewMode === 'category' ? ' is-active' : ''}`}
              onClick={() => {
                setViewMode('category');
                setOpenToolbarMenu(null);
              }}
            >
              <FontAwesomeIcon icon={faLayerGroup} className="me-2" />
              Group by Category
            </button>
            <button
              type="button"
              role="menuitem"
              className={`requirements-toolbar-menu-item${viewMode === 'due_date' ? ' is-active' : ''}`}
              onClick={() => {
                setViewMode('due_date');
                setOpenToolbarMenu(null);
              }}
            >
              <FontAwesomeIcon icon={faSort} className="me-2" />
              Sort by Due Date
            </button>
            <button
              type="button"
              role="menuitem"
              className={`requirements-toolbar-menu-item${viewMode === 'bank_account' ? ' is-active' : ''}`}
              onClick={() => {
                setViewMode('bank_account');
                setOpenToolbarMenu(null);
              }}
            >
              <FontAwesomeIcon icon={faUniversity} className="me-2" />
              Group by Bank Account
            </button>
          </RequirementsToolbarMenu>

          <RequirementsToolbarMenu
            label="Type"
            menuId="requirements-kind-filter-menu"
            isOpen={openToolbarMenu === 'kind'}
            onOpenChange={(open) => setOpenToolbarMenu(open ? 'kind' : null)}
          >
            {[
              ['all', 'All types'],
              ['allocation', 'Allocation'],
              ['bill_estimated', 'Bill (estimated)'],
              ['bill_actual', 'Bill (actual)'],
            ].map(([value, text]) => (
              <button
                key={value}
                type="button"
                role="menuitem"
                className={`requirements-toolbar-menu-item${kindFilter === value ? ' is-active' : ''}`}
                onClick={() => {
                  setKindFilter(value);
                  setOpenToolbarMenu(null);
                }}
              >
                {text}
              </button>
            ))}
          </RequirementsToolbarMenu>

          {viewMode !== 'bank_account' && (
            <Button
              variant="link"
              size="sm"
              className="requirements-toolbar-generate-subtle"
              onClick={handleGenerateAllDue}
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <FontAwesomeIcon icon={faFileInvoice} className="me-1" />
              )}
              Generate all due
            </Button>
          )}

          {viewMode !== 'bank_account' && (
            <Form.Check
              type="switch"
              id="show-inactive"
              label="Show inactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="requirements-toolbar-inactive-switch text-muted small mb-0"
            />
          )}
        </div>
      </div>

      {loading && filteredRequirements.length === 0 ? (
        <div className="text-center p-5"><Spinner animation="border" /></div>
      ) : viewMode === 'category' ? (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            handleDragOver(e);
          }}
          onDrop={(e) => {
            e.preventDefault();
            handleDrop(e);
          }}
          style={{ minHeight: '200px', position: 'relative' }}
        >
          {getRequirementsByCategory()
            .filter(({ requirements: categoryReqs }) => filterForFortnightView(categoryReqs).length > 0)
            .map(({ category, requirements: categoryReqs }, index) => {
              const filteredCategoryReqs = filterForFortnightView(categoryReqs);
              const categoryMeta = getCategoryGroupMeta(categoryReqs, category);
              const isExpanded = expandedCategories[category] === true;
              const showDropLineAbove = draggedOverItem?.id === categoryMeta.id && draggedOverItem?.type === 'category' && draggedOverItem?.index === index && draggedOverItem?.position === 'above';
              const showDropLineBelow = draggedOverItem?.id === categoryMeta.id && draggedOverItem?.type === 'category' && draggedOverItem?.index === index && draggedOverItem?.position === 'below';
              
              return (
                <div key={categoryMeta.id}>
                  {showDropLineAbove && (
                    <div 
                      style={{
                        height: '2px',
                        backgroundColor: '#007bff',
                        margin: '0.5rem 0',
                        position: 'relative',
                        zIndex: 1001
                      }}
                    />
                  )}
                  <div
                    draggable
                    onDragStart={(e) => {
                      handleDragStart(e, { id: categoryMeta.id, type: 'category', index });
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      handleDragOver(e, { id: categoryMeta.id, type: 'category', index });
                    }}
                    onDragEnd={(e) => {
                      handleDragEnd(e);
                    }}
                    style={{
                      opacity: isDragging && draggedItem?.id === categoryMeta.id ? 0.5 : 1,
                      transition: 'all 0.2s ease',
                      marginBottom: '1rem',
                      position: 'relative',
                      zIndex: isDragging && draggedItem?.id === categoryMeta.id ? 1000 : 1
                    }}
                  >
                    <div 
                      className="d-flex align-items-center mb-3" 
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleCategory(category)}
                    >
                      <div style={{ marginRight: '10px' }}>
                        <FontAwesomeIcon 
                          icon={faGripVertical}
                          style={{ 
                            color: '#6c757d',
                            fontSize: '1em'
                          }} 
                        />
                      </div>
                      <FontAwesomeIcon 
                        icon={getCategoryIcon(categoryMeta.icon)} 
                        style={{ 
                          color: categoryMeta.color,
                          fontSize: '1.2em',
                          marginRight: '10px'
                        }} 
                      />
                      <h4 className="mb-0" style={{ flex: 1 }}>{category}</h4>
                      {renderPeriodTotals(filteredCategoryReqs)}
                      <FontAwesomeIcon 
                        icon={isExpanded ? faChevronDown : faChevronRight} 
                        style={{ 
                          color: '#6c757d',
                          fontSize: '0.8em',
                          marginLeft: '10px'
                        }} 
                      />
                    </div>
                    {isExpanded && renderRequirementsTable(filteredCategoryReqs)}
                  </div>
                  {showDropLineBelow && (
                    <div 
                      style={{
                        height: '2px',
                        backgroundColor: '#007bff',
                        margin: '0.5rem 0',
                        position: 'relative',
                        zIndex: 1001
                      }}
                    />
                  )}
                </div>
              );
            })}
        </div>
      ) : viewMode === 'bank_account' ? (
        <>
          <BankAccountRequirementsView
            displayRequirements={displayRequirements}
            filteredRequirements={filteredRequirements}
            fortnightStart={fortnightStart}
            fortnightEnd={fortnightEnd}
            fortnightIndex={fortnightIndex}
            showInactive={showInactive}
            onShowInactiveChange={setShowInactive}
            expandedBankAccounts={expandedBankAccounts}
            onToggleAccount={toggleBankAccount}
            onEdit={onEdit}
            onAdvancedGenerate={handleAdvancedGenerate}
            onViewBills={handleViewBills}
            getCategoryIcon={getCategoryIcon}
            onSheetOpenChange={onBankSheetOpenChange}
          />
          <div className="requirements-bank-generate-footer">
            <Button
              variant="link"
              size="sm"
              className="requirements-toolbar-generate-subtle"
              onClick={handleGenerateAllDue}
              disabled={loading}
            >
              {loading ? (
                <Spinner animation="border" size="sm" className="me-1" />
              ) : (
                <FontAwesomeIcon icon={faFileInvoice} className="me-1" />
              )}
              Generate all due
            </Button>
          </div>
        </>
      ) : (
        // Render requirements sorted by due date
        renderRequirementsTable(filteredRequirements)
      )}

      <ConfirmationModal
        show={showGenerateAllModal}
        onHide={() => setShowGenerateAllModal(false)}
        onConfirm={handleGenerateAllDue}
        title="Generate All Due Bills"
        message="This will generate bills for all requirements that are due in the next 14 days. Continue?"
        confirmText="Generate Bills"
      />

      <GenerateBillsModal
        show={showAdvancedGenerateModal}
        onHide={() => setShowAdvancedGenerateModal(false)}
        requirementId={selectedRequirementId}
        onSuccess={handleAdvancedGenerateSuccess}
      />

      <RelatedBillsModal
        show={showRelatedBillsModal}
        onHide={() => setShowRelatedBillsModal(false)}
        requirementId={selectedRequirement?.id}
        requirementName={selectedRequirement?.name}
        categoryName={selectedRequirement?.category_name}
        categoryIcon={selectedRequirement?.category_details?.icon || 'money-bill'}
        categoryColor={selectedRequirement?.category_details?.color || '#6c757d'}
        onEdit={onEdit}
      />
    </div>
  );
};

RequirementsList.propTypes = {
  requirements: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    amount: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number
    ]).isRequired,
    frequency: PropTypes.string.isRequired,
    next_due_date: PropTypes.string,
    auto_generate: PropTypes.bool.isRequired,
    effective_from_date: PropTypes.string,
    effective_until_date: PropTypes.string,
    bank_account: PropTypes.number,
    category: PropTypes.number,
    notes: PropTypes.string,
    order: PropTypes.number
  })).isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onGenerateBills: PropTypes.func.isRequired,
  onGenerateAllDue: PropTypes.func.isRequired,
  onUpdateOrder: PropTypes.func.isRequired,
  defaultViewMode: PropTypes.string,
  fortnightIndex: PropTypes.number,
  fortnightDatesIndex: PropTypes.number,
  setFortnightIndex: PropTypes.func,
  formattedRangeFortnight: PropTypes.string,
  fortnightStart: PropTypes.instanceOf(Date),
  fortnightEnd: PropTypes.instanceOf(Date),
  onBankSheetOpenChange: PropTypes.func,
};

export default RequirementsList;
